import {
  type AnyAction,
  combineReducers,
  configureStore,
  type Middleware,
} from '@reduxjs/toolkit';
import webSocketEventsReducer from './api/web-socket/web-socket.slice';
import timelineReducer from './common-components/timeline/timeline.slice';
import securityRiskAssessmentReducer from './dossiers-and-panels/security-risk-assessments/security-risk-assessment.slice';
import threatAssessmentReducer from './dossiers-and-panels/threat-assessments/threat-assessment.slice';
import vesselDossierReducer from './dossiers-and-panels/vessel-dossier/vessel-dossier.slice';
import vulnerabilityAssessmentReducer from './dossiers-and-panels/vulnerability-assessments/vulnerability-assessment.slice';
import menuReducer from './main-menu/menu.slice';
import mapReducer from './map/map.slice';
import alertingPanelReducer from './maritime-menu-options/alert-panel/alertingVessels-panel.slice';
import drawingsPanelReducer from './maritime-menu-options/areas-panel/drawings.slice';
import avcsReducer from './maritime-menu-options/avcs-panel/avcs.slice';
import boundariesPanelReducer from './maritime-menu-options/boundaries-panel/boundaries-panel.slice';
import capellaPanelReducer from './maritime-menu-options/capella-panel/capella-panel.slice';
import citiesPanelReducer from './maritime-menu-options/cities-panel/cities-panel.slice';
import countriesPanelReducer from './maritime-menu-options/countries-panel/countries-panel.slice';
import documentsPanelReducer from './maritime-menu-options/documents-panel/documents-panel.slice';
import esaPanelReducer from './maritime-menu-options/esa-panel/esa-panel.slice';
import fleetsPanelReducer from './maritime-menu-options/fleets-panel/fleets-panel.slice';
import amendFleetDialogReducer from './maritime-menu-options/fleets-panel/manage-fleets/amend-fleet/amend-fleet-dialog.slice';
import createFleetsDialogReducer from './maritime-menu-options/fleets-panel/manage-fleets/create-fleets/create-fleets-dialog/create-fleets-dialog.slice';
import deleteFleetsDialogReducer from './maritime-menu-options/fleets-panel/manage-fleets/delete-fleets/delete-fleets-dialog.slice';
import fleetsListContextReducer from './maritime-menu-options/fleets-panel/manage-fleets/fleets-list-context/fleets-list-context.slice';
import manageFleetsReducer from './maritime-menu-options/fleets-panel/manage-fleets/manage-fleets.slice';
import moveFleetDialogReducer from './maritime-menu-options/fleets-panel/manage-fleets/move-to-company-fleet/move-to-company-fleet.slice';
import renameFleetDialogReducer from './maritime-menu-options/fleets-panel/manage-fleets/rename-fleet/rename-fleet-dialog/rename-fleet-dialog.slice';
import historyFiltersReducer from './maritime-menu-options/history-panel/history-filters/history-filters.slice';
import historyPanelReducer from './maritime-menu-options/history-panel/history-panel.slice';
import incidentsPanelReducer from './maritime-menu-options/incidents-panel/incident-panel.slice';
import maritimeIncidentsReducer from './maritime-menu-options/maritime-incidents/maritime-incidents.slice';
import myFleetPanelReducer from './maritime-menu-options/my-fleet-panel/myFleet-panel.slice';
import routesPanelReducer from './maritime-menu-options/routes-panel/routes-panel.slice';
import historicVesselsReducer from './maritime-menu-options/tools-panel/historic-area-search/historic-area-search.slice';
import nearbyVesselsReducer from './maritime-menu-options/tools-panel/nearby-vessels/nearby-vessels.slice';
import portsPanelReducer from './maritime-menu-options/world-ports-panel/world-ports-panel.slice';
import navHistoryReducer from './nav-history.slice';
import notePadReducer from './notepad/notepad.slice';
import searchPanelV2Reducer from './search-bar-v2/search-panel-v2.slice';
import adminReducer, {
  PermissionToggleNames,
  type PermissionToggles,
} from './settings/admin.slice';
import applySetupBarSliceReducer from './settings/apply-setup-bar.slice';
import userPermissionsReducer, {
  setUserPermissions,
} from './settings/user-permissions.slice';
import userPreferencesReducer from './settings/user-preferences.slice';
import alertingVesselsReducer from './state/alerts/alerts.slice';
import boundariesReducer from './state/boundaries/boundaries.slice';
import capellaReducer from './state/capella/capella.slice';
import citiesReducer from './state/cities/cities.slice';
import countriesReducer from './state/countries/countries.slice';
import documentsReducer from './state/documents/documents.slice';
import drawingsReducer from './state/drawings/drawings.slice';
import fleetsVesselsReducer from './state/fleet-vessels/fleet-vessels.slice';
import globalAisReducer from './state/global-ais/global-ais.slice';
import historyReducer from './state/history/history.slice';
import incidentsReducer from './state/incidents/incidents.slice';
import portsReducer from './state/ports/ports.slice';
import riMaritimeAreasReducer from './state/ri-maritime-areas/ri-maritime-areas.slice';
import routesReducer from './state/routes/routes.slice';
import toolsPanelReducer from './state/tools/tools.slice';
import myFleetReducer from './state/vessels/vessels.slice';
import weatherReducer from './state/weather/weather.slice';
import userReducer from './user/user.slice';

const appReducer = combineReducers({
  admin: adminReducer,
  applySetupBar: applySetupBarSliceReducer,
  alertingVessels: alertingVesselsReducer,
  alertingPanel: alertingPanelReducer,
  boundaries: boundariesReducer,
  boundariesPanel: boundariesPanelReducer,
  cities: citiesReducer,
  citiesPanel: citiesPanelReducer,
  countries: countriesReducer,
  countriesPanel: countriesPanelReducer,
  fleetsVessels: fleetsVesselsReducer,
  history: historyReducer,
  historyPanel: historyPanelReducer,
  historyFilters: historyFiltersReducer,
  incidents: incidentsReducer,
  maritimeIncidents: maritimeIncidentsReducer,
  incidentsPanel: incidentsPanelReducer,
  map: mapReducer,
  menu: menuReducer,
  myFleet: myFleetReducer,
  myFleetPanel: myFleetPanelReducer,
  navHistory: navHistoryReducer,
  ports: portsReducer,
  portsPanel: portsPanelReducer,
  drawings: drawingsReducer,
  drawingsPanel: drawingsPanelReducer,
  routes: routesReducer,
  routesPanel: routesPanelReducer,
  searchPanelV2: searchPanelV2Reducer,
  securityRiskAssessment: securityRiskAssessmentReducer,
  threatAssessment: threatAssessmentReducer,
  vulnerabilityAssessment: vulnerabilityAssessmentReducer,
  user: userReducer,
  userPreferences: userPreferencesReducer,
  userPermissions: userPermissionsReducer,
  vesselDossier: vesselDossierReducer,
  riMaritimeAreas: riMaritimeAreasReducer,
  webSocketEvents: webSocketEventsReducer,
  toolsPanel: toolsPanelReducer,
  documents: documentsReducer,
  documentsPanel: documentsPanelReducer,
  capella: capellaReducer,
  capellaPanel: capellaPanelReducer,
  nearbyVessels: nearbyVesselsReducer,
  historicVessels: historicVesselsReducer,
  timeline: timelineReducer,
  weather: weatherReducer,
  avcs: avcsReducer,
  globalAis: globalAisReducer,
  esaPanel: esaPanelReducer,
  notePad: notePadReducer,
  fleetsPanel: fleetsPanelReducer,
  fleetsListContext: fleetsListContextReducer,
  createFleetsDialog: createFleetsDialogReducer,
  renameFleetDialog: renameFleetDialogReducer,
  deleteFleetsDialog: deleteFleetsDialogReducer,
  manageFleets: manageFleetsReducer,
  amendFleetDialog: amendFleetDialogReducer,
  moveFleetDialog: moveFleetDialogReducer,
});

export const RESET_ACTION = {
  type: 'RESET',
};

type AppState = ReturnType<typeof appReducer>;

const rootReducer = (state: AppState | undefined, action: AnyAction) => {
  // https://stackoverflow.com/questions/35622588/how-to-reset-the-state-of-a-redux-store
  // reset state of Redux Store
  if (action.type === RESET_ACTION.type) {
    localStorage.clear();
    return appReducer(undefined, { type: '@@INIT' });
  }

  return appReducer(state, action);
};

// One-time flag for the middleware to set permissions in the test environment
let permissionsSet = false;

const generateAllPermissionsEnabled = (): PermissionToggles => {
  const permissions: PermissionToggles = {} as PermissionToggles;
  (
    Object.keys(PermissionToggleNames) as Array<
      keyof typeof PermissionToggleNames
    >
  ).forEach((key) => {
    permissions[key] = { enabled: true };
  });
  return permissions;
};

const testEnvironmentMiddleware: Middleware = (store) => (next) => (action) => {
  if (process.env.NODE_ENV === 'test' && !permissionsSet) {
    permissionsSet = true;
    store.dispatch(setUserPermissions(generateAllPermissionsEnabled()));
  }
  return next(action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // map: make map instance accessible to entire app. Allows us to perform map actions and listen to map events
        // selectedVessel: mapbox geojson feature - will allow us to modify properties or perform action on selected feature
        ignoredActions: ['map/setMap', 'vessel-dossier/setSelectedVessel'],
        ignoredPaths: ['map.map', 'vesselDossier.selectedVessel'],
      },
    }).concat(testEnvironmentMiddleware),
  devTools: {
    stateSanitizer: (state) => {
      const appState = state as AppState;
      return {
        ...state,
        map: {
          ...appState.map,
          map: '<<MAP INSTANCE>>' as any,
        },
      } as any;
    },
  },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export default store;
